import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { MapPin, Calendar } from 'lucide-react';
import { useUserPresence } from '@/hooks/useUserPresence';

interface SetLocationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const SetLocationDialog: React.FC<SetLocationDialogProps> = ({ open, onOpenChange }) => {
  const { toast } = useToast();
  const { updateMyStatus } = useUserPresence();
  const [location, setLocation] = useState('');
  const [message, setMessage] = useState('');
  const [duration, setDuration] = useState('2'); // jours
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!location.trim()) {
      toast({
        title: "Localisation requise",
        description: "Veuillez indiquer votre ville",
        variant: "destructive"
      });
      return;
    }

    setLoading(true);

    try {
      // Calculer la date d'expiration
      const availableUntil = new Date();
      availableUntil.setDate(availableUntil.getDate() + parseInt(duration));

      // Mettre à jour le statut avec la géolocalisation
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Non authentifié');

      const { error } = await supabase
        .from('user_presence')
        .update({
          status: 'traveling',
          current_location: location.trim(),
          location_message: message.trim() || null,
          available_until: availableUntil.toISOString(),
          notify_radius_km: 50
        })
        .eq('user_id', user.id);

      if (error) throw error;

      // Déclencher les notifications via edge function
      await supabase.functions.invoke('notify-location-members', {
        body: {
          userId: user.id,
          location: location.trim(),
          message: message.trim(),
          availableUntil: availableUntil.toISOString()
        }
      });

      toast({
        title: "✅ Disponibilité activée !",
        description: `Visible jusqu'au ${availableUntil.toLocaleDateString('fr-FR')}`
      });

      onOpenChange(false);
      setLocation('');
      setMessage('');
      setDuration('2');
    } catch (error) {
      console.error('Erreur activation disponibilité:', error);
      toast({
        title: "Erreur",
        description: "Impossible d'activer la disponibilité",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MapPin className="h-5 w-5 text-orange-600" />
            Je suis disponible ici
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          <div className="space-y-2">
            <Label htmlFor="location">
              Ville / Localisation *
            </Label>
            <Input
              id="location"
              placeholder="Ex: Alger, Algérie"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="duration">
              Durée de disponibilité
            </Label>
            <Select value={duration} onValueChange={setDuration}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="1">1 jour</SelectItem>
                <SelectItem value="2">2 jours</SelectItem>
                <SelectItem value="3">3 jours</SelectItem>
                <SelectItem value="5">5 jours</SelectItem>
                <SelectItem value="7">1 semaine</SelectItem>
                <SelectItem value="14">2 semaines</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="message">
              Message (optionnel)
            </Label>
            <Textarea
              id="message"
              placeholder="Ex: Disponible pour café/networking jusqu'à vendredi"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={3}
            />
          </div>

          <div className="bg-muted/50 p-3 rounded-lg text-sm space-y-1">
            <p className="font-medium flex items-center gap-2">
              <Calendar className="h-4 w-4" />
              Qui sera notifié ?
            </p>
            <p className="text-muted-foreground">
              • Les membres dans un rayon de 50km recevront une notification<br />
              • Tous les membres de votre pays verront votre annonce
            </p>
          </div>

          <div className="flex gap-2 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="flex-1"
            >
              Annuler
            </Button>
            <Button type="submit" disabled={loading} className="flex-1">
              {loading ? 'Activation...' : 'Activer ma disponibilité'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
